import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSampleUsers,
  deleteSampleUser,
  setFilters,
  setPagination,
} from '../../../Reducer/sampleUsersSlice';

import {
  Box,
  Button,
  Typography,
  Stack,
  Paper,
  CircularProgress,
  Alert,
  Pagination,
  Collapse,
  IconButton,
} from '@mui/material';

import FilterListIcon from '@mui/icons-material/FilterList';

import Table from './Table';
import Filters from './Filters';
import { toast } from 'react-toastify';

const DownloadSampleUserList = () => {
  const dispatch = useDispatch();
  const {
    data = [],
    filters = {},
    pagination = {},
    status,
    error,
  } = useSelector((state) => state.sampleUsers);

  const [downloadableData, setDownloadableData] = useState(new Map());
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false); // Toggle filter visibility

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchSampleUsers({ filters, pagination })).unwrap();
      } catch (err) {
        toast.error(`Failed to fetch users: ${err?.message || 'Unknown error'}`);
      }
    };
    fetchData();
  }, [filters, pagination, dispatch]);

  useEffect(() => {
    if (Array.isArray(data)) {
      setDownloadableData((prev) => {
        const newMap = new Map(prev);
        data.forEach((user) => newMap.set(user.id, user));
        return newMap;
      });
    }
  }, [data]);

  const handleDelete = async () => {
    const idsToDelete = Array.from(selectedRows);
    if (idsToDelete.length === 0) return;
    const confirmed = window.confirm('Are you sure you want to delete the selected user(s)?');
    if (!confirmed) return;

    try {
      const message = await dispatch(deleteSampleUser(idsToDelete)).unwrap();
      toast.success(message);
      await dispatch(fetchSampleUsers({ filters, pagination })).unwrap();
      setSelectedRows(new Set());
    } catch (err) {
      toast.error(`Deletion failed: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleFiltersChange = (newFilters) => {
    dispatch(setFilters(newFilters));
    dispatch(setPagination({ currentPage: 1 }));
    setDownloadableData(new Map());
    setSelectedRows(new Set());
  };

  const handlePageChange = (event, newPage) => {
    dispatch(setPagination({ currentPage: newPage }));
  };

  const handleDownload = () => {
    if (selectedRows.size === 0) {
      alert('No records selected for download.');
      return;
    }

    const selectedData = Array.from(downloadableData.values()).filter((user) =>
      selectedRows.has(user.id)
    );

    const csvData = [
      ['ID', 'User Name', 'Email', 'Event Name', 'Created At'],
      ...selectedData.map((user) => [
        user.id,
        user.user_name,
        user.user_email,
        user.product_name,
        new Date(user.createdAt).toLocaleString(),
      ]),
    ];

    const csvString = csvData.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'selected_records.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: '#fafafa',
        minHeight: '100vh',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Typography
          variant="h5"
          fontWeight={600}
          color="primary"
          sx={{ textAlign: { xs: 'center', md: 'left' } }}
        >
          Sample User List
        </Typography>
            

            
        <IconButton onClick={() => setShowFilters((prev) => !prev)} color="primary"> 
          <FilterListIcon />
        </IconButton>
      </Stack>

      <Collapse in={showFilters}>
        <Filters filters={filters} setFilters={handleFiltersChange} />
      </Collapse>

      {status === 'loading' && (
        <Stack alignItems="center" my={6}>
          <CircularProgress color="primary" />
        </Stack>
      )}

      {status === 'failed' && (
        <Alert severity="error" sx={{ my: 2 }}>
          Error: {error?.message || error || 'Something went wrong.'}
        </Alert>
      )}

      {status === 'succeeded' && data.length === 0 && (
        <Typography
          variant="body1"
          sx={{ my: 4, textAlign: 'center' }}
          color="text.secondary"
        >
          No users found.
        </Typography>
      )}

      {status === 'succeeded' && data.length > 0 && (
        <>
          {selectedRows.size > 0 && (
            <Paper
              elevation={2}
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: '#e3f2fd',
                border: '1px solid #90caf9',
                wordBreak: 'break-word',
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Selected IDs:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {Array.from(selectedRows).join(', ')}
              </Typography>
              <Button
                onClick={() => setSelectedRows(new Set())}
                variant="outlined"
                size="small"
                color="primary"
                sx={{ mt: 1 }}
              >
                Clear Selection
              </Button>
            </Paper>
          )}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            mb={2}
            spacing={{ xs: 1, sm: 0 }}
          >
            <Button
              variant="contained"
              onClick={handleDownload}
              disabled={selectedRows.size === 0}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                px: 3,
                py: 1,
                background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                boxShadow: '0 2px 10px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)',
                  boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)',
                },
                transition: 'all 0.3s ease',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Download Selected
            </Button>

            <Button
              onClick={handleDelete}
              variant="contained"
              color="error"
              disabled={selectedRows.size === 0}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Delete Selected Rows
            </Button>
          </Stack>

          <Paper
            elevation={3}
            sx={{
              p: 2,
              backgroundColor: '#fff',
              overflowX: 'auto',
            }}
          >
            <Table
              data={data}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              downloadableData={downloadableData}
              handleDelete={handleDelete}
            />
          </Paper>

          <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
            <Pagination
              count={Math.ceil(pagination.totalCount / pagination.pageSize)}
              page={pagination.currentPage}
              onChange={handlePageChange}
              color="primary"
              siblingCount={1}
              boundaryCount={1}
              size={window.innerWidth < 600 ? 'small' : 'medium'}
            />
          </Stack>
        </>
      )}
    </Box>
  );
};

export default DownloadSampleUserList;
